import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Grid,
} from '@mui/material';
import { Check, Plus, Lock, AlertCircle, FileText, CheckCircle2 } from 'lucide-react';
import { toast } from '../../../../../../../ui/toast';
import { createAssessmentTemplate } from '../../../../../../../utils/api_service/assessment_api';
import { AssessmentTemplateCreate, Question } from '../../../../../../../utils/types/assessment_types';

interface SaveTemplateProps {
  closeModal: () => void;
  onTemplateSave?: (templateData: AssessmentTemplateData) => void;
  questions: Question[];
  assessmentSettings?: AssessmentSettings;
  jobTitle: string;
  jobTitleId: string;
}

interface AssessmentTemplateData {
  id?: string;
  name: string;
  description: string;
  category: string;
  tags: string[];
  questions: Question[];
  totalQuestions: number;
  totalPoints: number;
  estimatedTime: string;
  isPrivate: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface AssessmentSettings {
  attemptCount?: string;
  customMessage?: string;
  enableCopy?: boolean;
  showScore?: boolean;
  shuffleQuestions?: boolean;
  blurVideo?: boolean;
  unsuccessfulEmail?: boolean;
  thresholdPercent?: string;
}

interface ExistingTemplate {
  id: string;
  name: string;
  description: string;
  questionsCount: number;
  lastUsed: string;
  category: string;
  isPrivate: boolean;
}

const existingTemplates: ExistingTemplate[] = [
  {
    id: '1',
    name: 'Frontend Developer Assessment',
    description: 'React, JavaScript, and CSS focused questions',
    questionsCount: 15,
    lastUsed: '2025-10-25',
    category: 'Engineering',
    isPrivate: false,
  },
  {
    id: '2',
    name: 'Backend Developer Assessment',
    description: 'Node.js, API, and database questions',
    questionsCount: 12,
    lastUsed: '2025-10-20',
    category: 'Engineering',
    isPrivate: false,
  },
  {
    id: '3',
    name: 'My Custom Assessment',
    description: 'Personal collection of questions',
    questionsCount: 8,
    lastUsed: '2025-10-18',
    category: 'Other',
    isPrivate: true,
  },
];

const categories = [
  'Engineering',
  'Design',
  'Product',
  'Data',
  'Marketing',
  'Sales',
  'Operations',
  'Finance',
  'HR',
  'Other',
];

export const SaveTemplate: React.FC<SaveTemplateProps> = ({
  closeModal,
  questions,
  jobTitle,
  jobTitleId,
}) => {
  const [saveMode, setSaveMode] = useState<'new' | 'existing'>('new');
  const [selectedTemplate, setSelectedTemplate] = useState<ExistingTemplate | null>(null);
  const [templateName, setTemplateName] = useState(jobTitle);
  const [templateDescription, setTemplateDescription] = useState(jobTitle);
  const [selectedCategory, setSelectedCategory] = useState('Other');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const today = new Date();
  const day = String(today.getDate()).padStart(2, '0');
  const month = today.toLocaleString('en-GB', { month: 'short' });
  const year = today.getFullYear();
  const formattedDate = `${day}/${month}/${year}`;
  const [saveObject, setSaveObject] = useState<AssessmentTemplateCreate>({
    templateTitle: `${jobTitle} - ${formattedDate}`,
    jobTitle: jobTitleId,
    questionAnswer: [],
  });

  useEffect(() => {
    if (questions.length > 0) {
      setSaveObject((prev) => ({
        ...prev,
        questionAnswer: questions.map((q) => q.id),
      }));
    }
  }, [questions]);

  const saveTemplate = async () => {
    try {
      setIsSaving(true);
      const response = await createAssessmentTemplate(saveObject);
      if (response.ok) {
        toast.success('Template created successfully!', {
          position: 'bottom-center',
        });
        closeModal();
      } else {
        toast.error('Failed to create template', { position: 'bottom-center' });
      }
    } catch {
      toast.error('Failed to create template', { position: 'bottom-center' });
    } finally {
      setIsSaving(false);
    }
  };

  const templateStats = useMemo(() => {
    const totalPoints = questions.reduce((sum, q) => sum + (q.points || 0), 0);
    const totalTimeSeconds = questions.reduce((sum, q) => sum + (q.time || 0), 0);
    const hours = Math.floor(totalTimeSeconds / 3600);
    const minutes = Math.floor((totalTimeSeconds % 3600) / 60);
    const estimatedTime = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

    return {
      totalQuestions: questions.length,
      totalPoints,
      estimatedTime,
    };
  }, [questions]);

  const filteredTemplates = useMemo(() => {
    return existingTemplates.filter(
      (template) =>
        template.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.description.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [searchTerm]);

  const handleTemplateSelect = (template: ExistingTemplate) => {
    setSelectedTemplate(template);
    setTemplateName(template.name);
    setTemplateDescription(template.description);
    setSelectedCategory(template.category);
    setIsPrivate(template.isPrivate);
  };

  const handleAddTag = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const newTag = tagInput.trim();
      if (newTag && !tags.includes(newTag) && tags.length < 8) {
        setTags([...tags, newTag]);
        setTagInput('');
      }
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', maxWidth: '1100px', mx: 'auto', gap: 3 }}>
      {/* Save Mode Selection */}
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1.5 }}>
          Save Options
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Paper
              variant="outlined"
              onClick={() => {
                setSaveMode('new');
                setSelectedTemplate(null);
                setTemplateName('');
                setTemplateDescription('');
                setSelectedCategory('Other');
                setTags([]);
                setIsPrivate(false);
              }}
              sx={{
                p: 2.5,
                cursor: 'pointer',
                borderRadius: 2,
                border: 2,
                borderColor: saveMode === 'new' ? 'primary.main' : 'divider',
                bgcolor: saveMode === 'new' ? 'primary.50' : 'background.paper',
                transition: 'all 0.2s',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    border: '2px solid',
                    borderColor: saveMode === 'new' ? 'primary.main' : 'grey.400',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {saveMode === 'new' && (
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
                  )}
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Create New Template
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 3.5 }}>
                Create a brand new assessment template with current questions
              </Typography>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Paper
              variant="outlined"
              onClick={() => setSaveMode('existing')}
              sx={{
                p: 2.5,
                cursor: 'pointer',
                borderRadius: 2,
                border: 2,
                borderColor: saveMode === 'existing' ? 'primary.main' : 'divider',
                bgcolor: saveMode === 'existing' ? 'primary.50' : 'background.paper',
                transition: 'all 0.2s',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    border: '2px solid',
                    borderColor: saveMode === 'existing' ? 'primary.main' : 'grey.400',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {saveMode === 'existing' && (
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main' }} />
                  )}
                </Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  Update Existing Template
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ ml: 3.5 }}>
                Replace questions in an existing template
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* Existing Template Selection */}
      {saveMode === 'existing' && (
        <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: 'action.hover' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', mb: 1.5 }}>
            Select Template to Update
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Search existing templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ mb: 2, bgcolor: 'background.paper' }}
          />
          <Box sx={{ maxHeight: 200, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {filteredTemplates.map((template) => {
              const isSelected = selectedTemplate?.id === template.id;
              return (
                <Paper
                  key={template.id}
                  variant="outlined"
                  onClick={() => handleTemplateSelect(template)}
                  sx={{
                    p: 2,
                    cursor: 'pointer',
                    borderRadius: 1.5,
                    border: 1.5,
                    borderColor: isSelected ? 'primary.main' : 'divider',
                    bgcolor: isSelected ? 'primary.50' : 'background.paper',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {template.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {template.description}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                      <Typography variant="caption" color="text.secondary">
                        {template.questionsCount} questions
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Last used: {template.lastUsed}
                      </Typography>
                      {template.isPrivate && (
                        <Chip
                          icon={<Lock size={12} />}
                          label="Private"
                          size="small"
                          color="warning"
                          variant="outlined"
                          sx={{ height: 20, fontSize: '10px' }}
                        />
                      )}
                    </Box>
                  </Box>
                  {isSelected && (
                    <Box
                      sx={{
                        width: 22,
                        height: 22,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        color: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Check size={14} />
                    </Box>
                  )}
                </Paper>
              );
            })}
            {filteredTemplates.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 3, color: 'text.secondary' }}>
                <FileText size={32} style={{ margin: '0 auto 8px', opacity: 0.5 }} />
                <Typography variant="body2">No templates found matching your search</Typography>
              </Box>
            )}
          </Box>
        </Paper>
      )}

      {/* Form Fields */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
            Template Name *
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={saveObject.templateTitle}
            onChange={(e) =>
              setSaveObject((prev) => ({
                ...prev,
                templateTitle: e.target.value,
              }))
            }
            placeholder="e.g., Senior Frontend Developer Assessment"
            inputProps={{ maxLength: 100 }}
            helperText={`${templateName.length}/100 characters`}
          />
        </Box>

        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
            Description *
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={templateDescription}
            onChange={(e) => setTemplateDescription(e.target.value)}
            placeholder="Describe what this template is for and when to use it..."
            inputProps={{ maxLength: 300 }}
            helperText={`${templateDescription.length}/300 characters`}
          />
        </Box>

        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
            Category
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              {categories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
            Tags (Optional)
          </Typography>
          <TextField
            fullWidth
            size="small"
            value={tagInput}
            onChange={(e) => setTagInput(e.target.value)}
            onKeyDown={handleAddTag}
            placeholder="Type a tag and press Enter (e.g., React, JavaScript, Senior Level)"
            disabled={tags.length >= 8}
            helperText={`${tags.length}/8 tags added. Press Enter or comma to add tags.`}
          />
          {tags.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
              {tags.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  onDelete={() => removeTag(tag)}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          )}
        </Box>

        <FormControlLabel
          control={
            <Checkbox
              checked={isPrivate}
              onChange={(e) => setIsPrivate(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.primary' }}>
                Make this template private
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Private templates are only visible to you. Uncheck to share with your team.
              </Typography>
            </Box>
          }
        />

        {/* Assessment Summary */}
        <Paper
          variant="outlined"
          sx={{
            p: 3,
            borderRadius: 2,
            bgcolor: 'action.hover',
            borderColor: 'primary.light',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <FileText size={20} color="#1976d2" />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Assessment Summary
            </Typography>
          </Box>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={4}>
              <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                  {templateStats.totalQuestions}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Questions
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={4}>
              <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                  {templateStats.totalPoints}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total Points
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={4}>
              <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                  {templateStats.estimatedTime}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Est. Time
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <Paper sx={{ p: 2, borderRadius: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary', mb: 1.5 }}>
              Template will include:
            </Typography>
            <Grid container spacing={1}>
              {[
                'All Questions & Answers',
                'Skills & Difficulty Levels',
                'Question Types & Media',
                'Time Limits & Scoring',
                'Question Order & Structure',
                'Sub-skills & Themes',
              ].map((item, idx) => (
                <Grid item xs={6} key={idx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle2 size={16} color="#2e7d32" />
                    <Typography variant="body2" color="text.secondary">
                      {item}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>

          {saveMode === 'existing' && selectedTemplate && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: 2,
                bgcolor: 'warning.50',
                border: 1,
                borderColor: 'warning.light',
                display: 'flex',
                gap: 1.5,
              }}
            >
              <AlertCircle size={20} color="#ed6c02" />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'warning.dark' }}>
                  Updating Existing Template
                </Typography>
                <Typography variant="body2" color="warning.dark" sx={{ mt: 0.5 }}>
                  This will replace all {selectedTemplate.questionsCount} questions in “{selectedTemplate.name}” with your current {templateStats.totalQuestions} questions.
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button
          variant="outlined"
          onClick={closeModal}
          disabled={isSaving}
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          Cancel
        </Button>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {saveMode === 'new' ? 'Creating new template' : 'Updating existing template'}
          </Typography>

          <Button
            variant="contained"
            onClick={saveTemplate}
            disabled={
              !templateName.trim() ||
              !templateDescription.trim() ||
              (saveMode === 'existing' && !selectedTemplate) ||
              isSaving ||
              questions.length === 0
            }
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Plus size={16} />}
            sx={{ textTransform: 'none', borderRadius: 2 }}
          >
            {isSaving
              ? saveMode === 'new' ? 'Creating Template...' : 'Updating Template...'
              : saveMode === 'new' ? 'Create Template' : 'Update Template'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};
export default SaveTemplate;
